import { Portal } from '@radix-ui/react-portal';
import {
  BodyText,
  Button,
  Card,
  DetailText,
  Divider,
  Flex,
  Link,
  SectionHeader,
} from '@utilitywarehouse/hearth-react';
import {
  BasketMediumIcon,
  ChevronDownSmallIcon,
  ChevronUpSmallIcon,
  ShareSmallIcon,
} from '@utilitywarehouse/hearth-react-icons';
import clsx from 'clsx';
import { FC, ReactElement, RefObject } from 'react';
import { useLocation } from 'react-router-dom';

import { Backdrop } from '@components/Backdrop';
import { paths } from '@constants/paths';
import { useSaveForLater } from '@hooks/useSaveForLater';
import { useSyncState } from '@hooks/useSyncState';
import { useAppointmentContext } from '@providers/AppointmentProvider/AppointmentContext';
import { useBasketContext } from '@providers/basket/BasketProvider/Context';
import { useBreakpoints } from '@utils/useBreakpoints';

import { BasketItemProps } from './BasketItem';
import './styles.scss';

interface BasketProps {
  total: string;
  children: ReactElement<BasketItemProps> | ReactElement<BasketItemProps>[];
  addOns?: ReactElement<BasketItemProps>[];
  loading?: boolean;
}

interface BasketUnwrappedProps extends BasketProps {
  collapsible: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  onClickSave: () => void;
  containerRef: RefObject<Nullable<HTMLDivElement>>;
}

interface BasketContentProps {
  children: ReactElement<BasketItemProps> | ReactElement<BasketItemProps>[];
  addOns?: ReactElement<BasketItemProps>[];
}

interface BasketTotalFooterProps {
  total: string;
}

const BasketContent: FC<BasketContentProps> = ({ children, addOns }) => (
  <Flex direction="column" spacing="xl" justifyContent="between">
    {children}
    {addOns && addOns.length > 0 && <Divider />}
    {addOns}
  </Flex>
);

const BasketTotalFooter: FC<BasketTotalFooterProps> = ({ total }) => (
  <Flex
    justifyContent="between"
    alignItems="center"
    padding="300"
    className="basket__summary"
  >
    <BodyText size="lg" weight="semibold">
      Total
    </BodyText>
    <DetailText size="xl" data-testid="basket-total">
      {total}
    </DetailText>
  </Flex>
);

const BasketUnwrapped: FC<BasketUnwrappedProps> = ({
  total,
  children,
  addOns,
  loading,
  collapsible,
  expanded,
  setExpanded,
  onClickSave,
  containerRef,
}) => {
  const location = useLocation();
  const { isSyncedAppointment } = useAppointmentContext();

  const showSave = !loading && location.pathname !== paths.saveForLater;

  // Desktop view
  if (!collapsible) {
    return (
      <Card
        variant="subtle"
        colorScheme="neutralStrong"
        direction="column"
        className="basket"
        data-testid="basket-component"
        paddingNone
      >
        <Flex direction="column" spacing="lg" padding="300">
          <SectionHeader
            heading="Monthly bill"
            trailingContent={
              showSave ? (
                <Link asChild>
                  <button type="button" onClick={onClickSave}>
                    <ShareSmallIcon />
                    Share
                  </button>
                </Link>
              ) : undefined
            }
          />
          <BasketContent addOns={addOns}>{children}</BasketContent>
        </Flex>
        <BasketTotalFooter total={total} />
      </Card>
    );
  }

  // Mobile view
  return (
    <Portal container={containerRef?.current}>
      <Backdrop visible={expanded} onClick={() => setExpanded(false)} />
      <div
        className={clsx('basket__panel', {
          'basket__panel--expanded': expanded,
          'basket__panel--with-session-control': isSyncedAppointment,
        })}
      >
        <Flex direction="column" padding="300" spacing="xl">
          <SectionHeader
            heading="Monthly bill"
            trailingContent={
              showSave ? (
                <Link asChild>
                  <button type="button" onClick={onClickSave}>
                    <ShareSmallIcon />
                    Share
                  </button>
                </Link>
              ) : undefined
            }
          />
          <BasketContent addOns={addOns}>{children}</BasketContent>
        </Flex>
      </div>
      <Flex
        justifyContent="between"
        alignItems="center"
        paddingY="150"
        paddingX="200"
        className="basket__sticky-bar"
        data-testid="basket-component"
      >
        <Button variant="ghost" onClick={() => setExpanded(!expanded)}>
          <BasketMediumIcon />
          {expanded ? 'Hide monthly bill' : 'View monthly bill'}
          {expanded ? <ChevronDownSmallIcon /> : <ChevronUpSmallIcon />}
        </Button>
        <DetailText size="xl" data-testid="basket-total">
          {total}
        </DetailText>
      </Flex>
    </Portal>
  );
};

const Basket: FC<BasketProps> = ({ children, ...rest }) => {
  const { breakpointsDown } = useBreakpoints();
  const collapsible = breakpointsDown.md;
  const { containerRef } = useBasketContext();
  const [expanded, setExpanded] = useSyncState<boolean>(
    'basket-expanded',
    false
  );
  const handleClickSave = useSaveForLater();

  return (
    <BasketUnwrapped
      {...rest}
      collapsible={collapsible}
      expanded={expanded}
      setExpanded={setExpanded}
      onClickSave={handleClickSave}
      containerRef={containerRef}
    >
      {children}
    </BasketUnwrapped>
  );
};

export { Basket, BasketUnwrapped };
